import isEqual from "lodash/isEqual";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocations } from "@/hooks/use-locations";
import { ActionsContext, type ActionsContextValue, FiltersContext, type FiltersContextValue, PanelContext, type PanelContextValue } from "./context";
import { isEmptyFilterValue } from "./helpers";
import type { FilterTypeConfig } from "./types";
const PENDING_FLUSH_DEBOUNCE_MS = 300;

/**
 * Filters emptied by applying an ID. An ID names one exact record, so every
 * other predicate on the page can only hide it — most often the location
 * filter, which seeds itself from the user's own location. Filters the page
 * marks `disableClear` are required rather than optional, so they stay put.
 */
function filtersClearedByIdFilter(filterTypes: FilterTypeConfig[], key: string, value: any, current: Record<string, any>): FilterTypeConfig[] {
  const applied = filterTypes.find(filterType => filterType.filterKey === key);
  if (applied?.type !== "id" || isEmptyFilterValue(value)) return [];
  return filterTypes.filter(filterType => filterType.filterKey !== key && !filterType.disableClear && !isEmptyFilterValue(current[filterType.filterKey]));
}
export interface TableFiltersProviderProps<T extends Record<string, any>> {
  filterTypes: FilterTypeConfig[];
  filters: T;
  onFilterChange: (key: keyof T, value: any) => void;
  children: ReactNode;
}
export function TableFiltersProvider<T extends Record<string, any>>({
  filterTypes,
  filters,
  onFilterChange,
  children
}: TableFiltersProviderProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInputFocused, setSearchInputFocused] = useState(false);
  const [pendingFilters, setPendingFilters] = useState<Partial<T>>(filters);
  const pendingFiltersRef = useRef<Partial<T>>(filters);
  const touchedKeysRef = useRef<Set<string>>(new Set());
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const {
    locations
  } = useLocations();
  const enhancedFilterTypes = useMemo(() => filterTypes.map(filterType => filterType.type === "location" && filterType.options === undefined ? {
    ...filterType,
    options: locations.map(location => ({
      label: location.name,
      value: location.id
    }))
  } : filterType), [filterTypes, locations]);

  // Refs so the async debounce flush always sees the latest committed state
  // and onFilterChange without re-creating the timer on every parent render.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;
  const cancelFlushTimer = useCallback(() => {
    if (flushTimerRef.current !== null) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
  }, []);

  // Flush touched keys whose final value differs from the committed state.
  // Toggling a checkbox twice (or otherwise landing back on the original)
  // shouldn't trigger a refetch, and empty values are normalized to undefined
  // so consumers see a consistent shape.
  const flushPending = useCallback(() => {
    const pending = pendingFiltersRef.current as Record<string, any>;
    const committed = filtersRef.current as Record<string, any>;
    touchedKeysRef.current.forEach(key => {
      const raw = pending[key];
      const next = raw === "" || Array.isArray(raw) && raw.length === 0 ? undefined : raw;
      if (!isEqual(next, committed[key])) {
        onFilterChangeRef.current(key as keyof T, next);
      }
    });
  }, []);
  const setOpen = useCallback((open: boolean) => {
    setIsOpen(open);
    cancelFlushTimer();
    if (open) {
      pendingFiltersRef.current = filters;
      setPendingFilters(filters);
      touchedKeysRef.current = new Set();
    } else {
      flushPending();
      touchedKeysRef.current = new Set();
    }
  }, [filters, cancelFlushTimer, flushPending]);
  const updatePending = useCallback((key_0: string, value: any) => {
    // The pending buffer starts as a copy of the committed filters, so it is
    // the live view of what the open dropdown is editing.
    const cleared = filtersClearedByIdFilter(enhancedFilterTypes, key_0, value, pendingFiltersRef.current as Record<string, any>);
    pendingFiltersRef.current = {
      ...pendingFiltersRef.current,
      ...Object.fromEntries(cleared.map(filterType_0 => [filterType_0.filterKey, undefined])),
      [key_0]: value
    };
    setPendingFilters(pendingFiltersRef.current);
    cleared.forEach(filterType_1 => touchedKeysRef.current.add(filterType_1.filterKey));
    touchedKeysRef.current.add(key_0);
    // Debounce-flush so consumers see edits ~300ms after the user pauses,
    // rather than waiting for the dropdown to close. Close still flushes
    // synchronously — whichever fires first wins.
    cancelFlushTimer();
    const waitForClose = [...touchedKeysRef.current].some(touched => enhancedFilterTypes.find(filterType_2 => filterType_2.filterKey === touched)?.commitOnClose);
    if (waitForClose) return;
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null;
      flushPending();
    }, PENDING_FLUSH_DEBOUNCE_MS);
  }, [cancelFlushTimer, enhancedFilterTypes, flushPending]);
  const commitImmediate = useCallback((key_1: string, value_0: any) => {
    filtersClearedByIdFilter(enhancedFilterTypes, key_1, value_0, filtersRef.current as Record<string, any>).forEach(filterType_3 => onFilterChange(filterType_3.filterKey as keyof T, undefined));
    onFilterChange(key_1 as keyof T, value_0);
  }, [enhancedFilterTypes, onFilterChange]);
  const dropTouched = useCallback((key_2: string) => {
    touchedKeysRef.current.delete(key_2);
  }, []);
  const focusSearch = useCallback(() => {
    searchInputRef.current?.focus();
  }, []);
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => searchInputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
    setSearchQuery("");
    return undefined;
  }, [isOpen]);
  useEffect(() => () => cancelFlushTimer(), [cancelFlushTimer]);
  const effectiveFilters = useMemo<Record<string, any>>(() => {
    if (!isOpen) return filters;
    return {
      ...(filters as Record<string, any>),
      ...Object.fromEntries(Array.from(touchedKeysRef.current, k => [k, (pendingFilters as Record<string, any>)[k]]))
    };
  }, [isOpen, filters, pendingFilters]);
  const filtersValue = useMemo<FiltersContextValue<T>>(() => ({
    filters,
    enhancedFilterTypes,
    filterTypes,
    onFilterChange
  }), [filters, enhancedFilterTypes, filterTypes, onFilterChange]);
  const panelValue = useMemo<PanelContextValue>(() => ({
    isOpen,
    searchQuery,
    pendingFilters: pendingFilters as Record<string, any>,
    effectiveFilters,
    searchInputRef,
    searchInputFocused
  }), [isOpen, searchQuery, pendingFilters, effectiveFilters, searchInputFocused]);
  const actionsValue = useMemo<ActionsContextValue>(() => ({
    setOpen,
    setSearchQuery,
    updatePending,
    commitImmediate,
    dropTouched,
    focusSearch,
    setSearchInputFocused
  }), [setOpen, updatePending, commitImmediate, dropTouched, focusSearch]);
  return <FiltersContext value={filtersValue}>
      <PanelContext value={panelValue}>
        <ActionsContext value={actionsValue}>{children}</ActionsContext>
      </PanelContext>
    </FiltersContext>;
}